'use client';

/**
 * useLiveQuotes — subscribe to MANY instruments over ONE WebSocket.
 *
 * The single-instrument useLiveQuote opens one socket per hook; a market-watch
 * of N rows would open N sockets and re-stress the engine's single event loop.
 * This opens ONE socket, subscribes every instrument on it (the backend filters
 * per connection by SubscriptionKey), and merges partial-delta ticks per
 * instrument (XTS feeds send ltp and bid/ask in separate updates).
 *
 *     const { quotes, status } = useLiveQuotes(ids, 'touchline');
 *     const q = quotes[instrumentId];   // { ltp, bid, ask, ... } | undefined
 */
import * as React from 'react';

import { getApiClientConfig } from '@/lib/api/client';
import { useSessionStore } from '@/lib/auth/session';
import type { LiveQuote, LiveQuoteCode, LiveQuoteStatus } from './use-live-quote';

function deriveWsUrl(): string {
  // The quotes WS may live on a separate host — the DATA plane, after the
  // control/data-plane split (PLANE_SPLIT.md). NEXT_PUBLIC_QUOTES_WS_URL overrides;
  // default = the API host (single-process, unchanged).
  const override = process.env.NEXT_PUBLIC_QUOTES_WS_URL;
  if (override) {return override.replace(/\/$/, '');}
  return getApiClientConfig().baseUrl.replace(/^http/, 'ws');
}

export function useLiveQuotes(
  instrumentIds: string[],
  code: LiveQuoteCode = 'touchline',
): { quotes: Record<string, LiveQuote>; status: LiveQuoteStatus } {
  const apiKey = useSessionStore((s) => s.accessToken);
  const [quotes, setQuotes] = React.useState<Record<string, LiveQuote>>({});
  const [status, setStatus] = React.useState<LiveQuoteStatus>('idle');

  // Stable signal for "the set of ids changed" (array identity churns each render).
  const idsKey = React.useMemo(
    () => Array.from(new Set(instrumentIds)).sort().join(','),
    [instrumentIds],
  );

  const wsRef = React.useRef<WebSocket | null>(null);
  const readyRef = React.useRef(false);
  const subscribedRef = React.useRef<Set<string>>(new Set());
  const idsRef = React.useRef<string[]>(instrumentIds);
  idsRef.current = instrumentIds;

  // ── One socket per (apiKey, code) ──────────────────────────────────────────
  React.useEffect(() => {
    if (!apiKey) {
      setStatus('idle');
      return;
    }
    setQuotes({});
    const token = apiKey;   // narrowed by the guard above; nested connect() can't re-narrow apiKey

    let unmounted = false;
    let heartbeat: ReturnType<typeof setInterval> | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;

    const clearHb = () => {
      if (heartbeat !== null) { clearInterval(heartbeat); heartbeat = null; }
    };
    const clearReconnect = () => {
      if (reconnectTimer !== null) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    };
    // Auto-reconnect after a dropped socket (tab backgrounding throttles the
    // heartbeat → the backend closes the lease). Bounded backoff 1s→2s→…→15s,
    // reset on subscribe. The new socket's `ack` re-subscribes every current id.
    const scheduleReconnect = () => {
      if (unmounted || reconnectTimer !== null) {return;}
      const delay = Math.min(15_000, 1_000 * 2 ** attempt);
      attempt += 1;
      reconnectTimer = setTimeout(() => { reconnectTimer = null; connect(); }, delay);
    };

    function connect() {
      if (unmounted) {return;}
      setStatus('connecting');
      subscribedRef.current = new Set();   // fresh socket → re-subscribe all on ack
      readyRef.current = false;
      let sock: WebSocket;
      try {
        // Token goes in the auth-first-frame (below), NOT the URL — a query-string
        // secret leaks into proxy/server access logs (backend audit M8).
        sock = new WebSocket(`${deriveWsUrl()}/ws/quotes`);
      } catch {
        setStatus('error');
        scheduleReconnect();
        return;
      }
      wsRef.current = sock;
      const send = (obj: unknown) => {
        if (sock.readyState === WebSocket.OPEN) {sock.send(JSON.stringify(obj));}
      };
      // Authenticate in-band as the FIRST frame; the server replies with `ack`.
      sock.onopen = () => { send({ type: 'auth', token }); };
      sock.onmessage = (e) => {
        if (unmounted) {return;}
        let msg: { type?: string } & Partial<LiveQuote>;
        try { msg = JSON.parse(e.data); } catch { return; }
        switch (msg.type) {
          case 'ack':
            readyRef.current = true;
            for (const id of Array.from(new Set(idsRef.current))) {
              send({ type: 'subscribe', instrument_id: id, code });
              subscribedRef.current.add(id);
            }
            break;
          case 'subscribed':
            setStatus('subscribed');
            attempt = 0;   // healthy → reset the backoff
            if (heartbeat === null) {
              heartbeat = setInterval(() => {
                if (unmounted) { clearHb(); return; }
                send({ type: 'heartbeat' });
              }, 15_000);
            }
            break;
          case 'quote': {
            const id = msg.instrument_id;
            if (!id) {break;}
            setQuotes((prev) => {
              const old = prev[id];
              const merged: LiveQuote = {
                instrument_id: id,
                code: msg.code ?? old?.code ?? code,
                ts: msg.ts ?? old?.ts ?? '',
                ltp: msg.ltp ?? old?.ltp ?? null,
                bid: msg.bid ?? old?.bid ?? null,
                ask: msg.ask ?? old?.ask ?? null,
                bid_qty: msg.bid_qty ?? old?.bid_qty ?? null,
                ask_qty: msg.ask_qty ?? old?.ask_qty ?? null,
                volume: msg.volume ?? old?.volume ?? null,
                open_interest: msg.open_interest ?? old?.open_interest ?? null,
                close: msg.close ?? old?.close ?? null,
                pct_change: msg.pct_change ?? old?.pct_change ?? null,
              };
              return { ...prev, [id]: merged };
            });
            break;
          }
          case 'error':
            console.warn('useLiveQuotes: server error', msg);
            break;
        }
      };
      sock.onerror = () => { if (!unmounted) {setStatus('error');} };
      sock.onclose = () => {
        clearHb();
        readyRef.current = false;
        if (!unmounted) { setStatus('disconnected'); scheduleReconnect(); }
      };
    }

    // On tab-refocus, RESTORE the feed. Two failure modes after a background spell:
    //   1. socket closed → reconnect (re-subscribes on ack);
    //   2. socket still OPEN but the backend expired our per-subscription leases (the
    //      heartbeat got throttled below the 30s TTL) → re-subscribe EVERY current id
    //      so the OLD ones resume (new ids added by scrolling already re-subscribed).
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || unmounted) {return;}
      const sock = wsRef.current;
      if (!sock || sock.readyState === WebSocket.CLOSED || sock.readyState === WebSocket.CLOSING) {
        clearReconnect();
        attempt = 0;
        connect();
      } else if (sock.readyState === WebSocket.OPEN) {
        for (const id of Array.from(new Set(idsRef.current))) {
          sock.send(JSON.stringify({ type: 'subscribe', instrument_id: id, code }));
          subscribedRef.current.add(id);
        }
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    connect();

    return () => {
      unmounted = true;
      document.removeEventListener('visibilitychange', onVisible);
      clearHb();
      clearReconnect();
      try { wsRef.current?.close(); } catch { /* noop */ }
      wsRef.current = null;
      readyRef.current = false;
      subscribedRef.current = new Set();
    };
  }, [apiKey, code]);

  // ── Diff subscriptions when the id set changes (no reconnect) ──────────────
  React.useEffect(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !readyRef.current) {return;}
    const want = new Set(instrumentIds);
    const have = subscribedRef.current;
    for (const id of want) {
      if (!have.has(id)) {
        ws.send(JSON.stringify({ type: 'subscribe', instrument_id: id, code }));
        have.add(id);
      }
    }
    for (const id of Array.from(have)) {
      if (!want.has(id)) {
        try {
          ws.send(JSON.stringify({ type: 'unsubscribe', instrument_id: id, code }));
        } catch {
          /* socket may be closing */
        }
        have.delete(id);
        setQuotes((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }
    }
    // idsKey is the stable trigger; instrumentIds read for the actual ids.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, code]);

  return { quotes, status };
}
